// eslint-disable-next-line id-length
import _ from 'lodash';
import { createStore } from 'vuex';

import Api from '@/manager/services/bright_finder.js';
import SegmentService from '@/shared/services/analytics/SegmentService';

const defaultState = {
  ageGroups: [],
  appBarBack: null,
  appBarChip: null,
  brand: {},
  config: {},
  currentPageTabs: null,
  currentPageTitle: null,
  default_locale: 'en',
  drawer: null,
  employments: [],
  features: {},
  filters: {},
  identified: false,
  languages: [],
  miniNav: false,
  newMessage: false,
  pages: {},
  profile: null,
  providers: [],
  providerRequestableFormSchemas: [],
  role: 'manager',
  schemas: {},
  site: null,
  siteWebpageId: null,
  userSessionTimeout: null,
  userSessionActionTimestamp: null,
};

export default createStore({
  actions: {
    page() {
      if (window.ga) {
        window.ga('set', 'page', window.location.pathname);
        window.ga('send', 'pageview');
      }
    },

    identify(context, args = {}) {
      Api.member.session.get(
        'manager',
        (response) => {
          const authenticated =
            response.data?.status === 'authenticated' ||
            response.data?.status === 'mfa_register' ||
            response.data?.status === 'password_reset';
          if (authenticated) {
            Api.manager.profile.getSelf({
              successCallback: (profileResp) => {
                if (profileResp?.data.organization_id == window.organization_id) {
                  context.dispatch('setProfileAndTimeout', profileResp.data);

                  if (args.success) {
                    SegmentService.forProvider(window.analytics).identifyMember(profileResp.data);
                    args.success(profileResp.data);
                  }
                } else if (args.failure) {
                  args.failure();
                }
              },
              failureCallback: () => {
                if (args.failure) {
                  args.failure();
                }
              },
            });
          } else if (args.failure) {
            args.failure();
          }
        },
        () => {
          args.failure();
        },
      );
    },

    async setProfileAndTimeout(context, profile) {
      await context.commit('setProfile', profile);
      context.commit('resetTimeout');
    },
  },

  getters: {
    currentEmployment: (state) => () => {
      return (
        state.profile?.employments?.find((employment) => !employment.end_date) ||
        state.profile?.employments?.[0]
      );
    },
    showBusinessAdminPages: (state) => () => {
      // True so long as *any* active employment has an admin role
      return state.profile?.employments?.find(
        (employment) => !employment.end_date && employment.business_role === 'Admin',
      );
    },
    hasBusinessMessagingAccess: (state) => () => {
      // True so long as *any* active employment has business_messages_access
      return state.profile?.employments?.find(
        (employment) => !employment.end_date && employment.business_messages_access,
      );
    },
    defaultLandingPage: (state) => () => {
      const isEmployed = !!state.profile?.employments?.find((employment) => !employment.end_date);

      if (isEmployed) {
        return { path: state.site.landing_page || '/providers/list' };
      }

      // An onboarding form is a required flow, so unemployed members are sent
      // straight to it rather than the location-less landing dashboard.
      if (state.config.provider_onboarding_form_schema_id) {
        return { name: 'RegisterAccess' };
      }

      return { name: 'ProviderLanding' };
    },
    isEmployed: (state) => () =>
      !!state.profile?.employments?.find((employment) => !employment.end_date),
    hasProviderRequestableFormSchemas: (state) => () =>
      !!state.providerRequestableFormSchemas?.length,
    needsOnboarding: (state) => () =>
      // The member has no employments or all existing employments are terminated,
      // and a provider onboarding form schema is configured
      !!state.config.provider_onboarding_form_schema_id &&
      (!state.profile?.employments?.length ||
        state.profile?.employments?.every((employment) => employment.end_date)),

    // App.vue hides ManagerMainNav on registration routes and omits the V2 shell on Welcome;
    // `route` lets this getter return no tabs if those rules ever diverge.
    primaryNavigationOptions: (state, getters) => (route) => {
      if (route && (route.meta?.registration || route.name === 'Welcome')) {
        return { tabs: [] };
      }

      const navOptions = {
        tabs: [],
      };
      // Main navigation options
      if (getters.isEmployed()) {
        // LOCATIONS
        if (state.pages.Provider.enabled) {
          navOptions.tabs.push({
            title: state.pages.Provider.title || 'Locations',
            icon: 'home',
            route: 'ProviderIndex',
          });
        }
        // INTERESTED (Children)
        if (state.pages.Interested.enabled) {
          navOptions.tabs.push({
            title: state.pages.Interested.title || 'Interested',
            icon: 'face',
            route: 'InterestedIndex',
          });
        }
        // STUDENTS (Enrolled)
        if (state.pages.Students.enabled) {
          navOptions.tabs.push({
            title: state.pages.Students.title || 'Students',
            icon: 'school',
            route: 'EnrollmentIndex',
          });
        }
        // REQUESTS
        if (getters.hasProviderRequestableFormSchemas()) {
          navOptions.tabs.push({
            title: state.pages.Requests.title || 'Requests',
            icon: 'article',
            route: 'FormRequestIndex',
          });
        }
        // ASSESSMENTS
        if (state.pages.Assessments.enabled) {
          navOptions.tabs.push({
            title: state.pages.Assessments.title || 'Assessments',
            icon: 'star_rate',
            route: 'AssessmentIndex',
          });
        }
        // GRANTS
        if (state.pages.Grants.enabled) {
          navOptions.tabs.push({
            title: state.pages.Grants.title || 'Grants',
            icon: 'account_balance',
            route: 'GrantProgramIndex',
          });
        }
      }
      // LEARNING
      if (state.pages.Learning.enabled && state.profile?.enable_courses) {
        navOptions.tabs.push({
          title: state.pages.Learning.title || 'Learning',
          icon: 'book',
          route: 'CourseIndex',
        });
      }
      // BILLING CENTER
      if (state.features.enable_payments) {
        navOptions.tabs.push({
          title: 'Billing Center',
          icon: 'paid',
          route: 'BillingCenterIndex',
        });
      }
      // LICENSES
      if (state.features.enable_provider_licenses) {
        navOptions.tabs.push({
          title: 'Licenses',
          icon: 'verified',
          route: 'ProviderLicenseIndex',
        });
      }
      // MESSAGES
      if (
        getters.isEmployed() &&
        state.pages.Messaging.enabled &&
        getters.hasBusinessMessagingAccess()
      ) {
        navOptions.tabs.push({
          title: 'Messages',
          icon: 'message',
          route: 'MessageIndex',
        });
      }

      return navOptions;
    },
    accountSettingNavOptions: (state, getters) => () => {
      const navOptions = {
        backRoute: getters.defaultLandingPage(),
        tabs: [],
      };

      if (state.pages.Settings.enabled && getters.showBusinessAdminPages()) {
        navOptions.tabs.push(
          { title: 'Business information', icon: 'business', route: 'BusinessShow' },
          { title: 'Staff members', icon: 'people', route: 'ProviderStaffIndex' },
        );
      }
      navOptions.tabs.push({ title: 'My contact', icon: 'contact_page', route: 'ManagerAccount' });

      return navOptions;
    },
    providerProfileNavOptions: (state, getters) => (provider, terms) => {
      const schemaMeta = state.schemas[provider?.schema_id]?.meta;
      const backTitle = `Back to ${state.pages?.Providers?.title || 'Locations'}`;
      const navOptions = {
        queryDriven: true,
        backTitle,
        backRoute: getters.defaultLandingPage(),
        tabs: [],
      };
      if (!schemaMeta) return navOptions;

      if (schemaMeta?.overview?.enabled) {
        navOptions.tabs.push({ title: 'Overview', queryValue: 'overview', hideIcon: true });
      }

      if (schemaMeta?.openings?.enabled && !state.config.enable_combined_openings_and_enrollments) {
        navOptions.tabs.push({ title: 'Openings', queryValue: 'openings' });
      } else if (state.config.enable_combined_openings_and_enrollments) {
        navOptions.tabs.push({
          title: 'Openings & Enrollments',
          queryValue: 'openings-and-enrollments',
        });
      }

      if (schemaMeta?.photos?.enabled) {
        navOptions.tabs.push({ title: 'Photos', queryValue: 'photos' });
      }

      if (schemaMeta?.video?.enabled) {
        navOptions.tabs.push({ title: 'Video tour', queryValue: 'video' });
      }

      if (schemaMeta?.profile?.enable_description) {
        navOptions.tabs.push({ title: 'Description', queryValue: 'description' });
      }

      navOptions.tabs.push({ title: 'Contact', queryValue: 'contact' });

      if (schemaMeta.programs.enabled) {
        navOptions.tabs.push({
          title: terms.programs,
          description: schemaMeta.programs.description,
          queryValue: 'programs',
        });
      }

      if (schemaMeta?.tours?.enabled) {
        navOptions.tabs.push({ title: 'Tour requests', queryValue: 'tours' });
      }

      if (schemaMeta?.enrollment_steps?.enabled) {
        navOptions.tabs.push({
          title: `How to ${terms.enroll.toLowerCase()}`,
          queryValue: 'enrollment_steps',
        });
      }

      if (schemaMeta?.profile?.enable_emergency_response) {
        navOptions.tabs.push({ title: 'COVID-19', queryValue: 'emergency' });
      }

      navOptions.tabs.push({ title: 'Hours', queryValue: 'hours' });

      if (schemaMeta?.profile?.enable_payment_details) {
        navOptions.tabs.push({ title: 'Financial programs', queryValue: 'financial' });
      }

      if (
        schemaMeta?.enrollment?.enabled &&
        !state.config.enable_combined_openings_and_enrollments
      ) {
        navOptions.tabs.push({ title: 'Enrollment', queryValue: 'enrollment' });
      }

      if (schemaMeta?.profile?.enable_highlights) {
        navOptions.tabs.push({ title: 'Highlights', queryValue: 'highlights' });
      }

      if (schemaMeta?.staff?.enabled) {
        navOptions.tabs.push({ title: 'Staff', queryValue: 'staff' });
      }

      if (schemaMeta?.licensing?.enabled) {
        navOptions.tabs.push({ title: 'Licensing', queryValue: 'licensing', locked: true });
      }
      return navOptions;
    },
  },

  state: {
    ...defaultState,
  },

  mutations: {
    hide(state, dialog) {
      state[`${dialog}IsVisible`] = false;
    },

    setAgeGroups(state, ageGroups) {
      state.ageGroups = ageGroups;
    },

    setAppBarBack(state, route) {
      state.appBarBack = route;
    },

    setAppBarChip(state, chip) {
      state.appBarChip = chip;
    },

    setBrand(state, brand) {
      state.brand = brand;
    },

    setConfig(state, config) {
      state.config = config;
    },

    setCurrentPageTabs(state, tabs) {
      state.currentPageTabs = tabs;
    },

    setCurrentPageTitle(state, title) {
      state.currentPageTitle = title;
    },

    setDrawer(state, drawer) {
      state.drawer = drawer;
    },

    setFeatures(state, features) {
      state.features = features;
    },

    setFilters(state, args) {
      state.filters[args[0]] = args[1];
    },

    setLanguages(state, languages) {
      state.languages = languages;
    },

    setMiniNav(state, active) {
      state.miniNav = active;
    },

    setNewMessage(state, val) {
      state.newMessage = val;
    },

    setRole(state, role) {
      state.role = role;
    },

    setSchema(state, schema) {
      state.schemas ||= {};
      state.schemas[schema.id] = schema;
    },

    setSchemas(state, schemas) {
      state.schemas = schemas;

      if (schemas) {
        state.providerRequestableFormSchemas = Object.values(schemas).filter(
          (schema) => schema.data_type === 'Form' && schema.meta.provider_requestable,
        );
      }
    },

    setSiteWebpageId(state, siteWebpageId) {
      state.siteWebpageId = siteWebpageId;
    },

    setUserSessionTimeout(state, userSessionTimeout) {
      state.userSessionTimeout = userSessionTimeout;
    },

    setPages(state, pages) {
      state.pages = pages;
    },

    setProfile(state, profile) {
      state.profile = profile;
      if (profile && profile.default_locale) {
        state.default_locale = profile.default_locale;
      }
    },

    setProviders(state, providers) {
      state.providers = providers;
    },

    setSite(state, site) {
      state.site = site;
    },

    show(state, dialog) {
      state[`${dialog}IsVisible`] = true;
    },

    resetState(state) {
      if (import.meta.env.NODE_ENV !== 'test')
        throw new Error('resetState should not be called in production');

      Object.assign(state, defaultState);
    },

    /* eslint no-param-reassign: ["error", { "props": false }] */
    // resetTimeout: _.throttle((state) => {
    //   if (!!state.userSessionTimeout && !!state.profile) {
    //     Api.member.session.get('manager', (resp) => {
    //       if (resp.data?.status === 'authenticated') state.userSessionActionTimestamp = new Date();
    //     });
    //   }
    // }, 15000),
    resetTimeout() {
      return false;
    },
  },
});
